import { type PayloadAction } from '@reduxjs/toolkit';
import type { TSortableInput } from 'components/TextBubbleDragAndDrop';
import { EQuestionType, type TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import type { QuestionEditorsState } from './Question.Editor.Slice';
import { convertTexts } from './questionEditorThunks';

export type TextType = 'intro' | 'text' | 'outro' | 'clarify';

export const questionTextReducer = {
  resetText: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string; textType: TextType }>) => {
    const { questionId, textType } = action.payload;
    if (state.byId[questionId]) {
      const currentQuestion = state.byId[questionId];
      const { text, intro, outro, clarify } = currentQuestion.questionConfig;

      if (textType === 'text') {
        const originalText = text?.[currentQuestion.language]
          ? {
              isDefault: text[currentQuestion.language].isDefault ?? true,
              value: convertTexts(text[currentQuestion.language] ?? []),
            }
          : { isDefault: true, value: [] };

        state.byId[questionId] = {
          ...currentQuestion,
          text: { ...originalText },
          texts: {
            ...currentQuestion.texts,
            [currentQuestion.language]: originalText,
          },
        };
      } else if (textType === 'intro') {
        const originalText = intro?.[currentQuestion.language]
          ? {
              isDefault: intro[currentQuestion.language].isDefault ?? true,
              value: convertTexts(intro[currentQuestion.language] ?? []),
            }
          : { isDefault: true, value: [] };

        state.byId[questionId] = {
          ...currentQuestion,
          intro: { ...originalText },
        };
      } else if (textType === 'outro') {
        const originalText = outro?.[currentQuestion.language]
          ? {
              isDefault: outro[currentQuestion.language].isDefault ?? true,
              value: convertTexts(outro[currentQuestion.language] ?? []),
            }
          : { isDefault: true, value: [] };

        state.byId[questionId] = {
          ...currentQuestion,
          outro: { ...originalText },
        };
      } else if (textType === 'clarify') {
        const originalText = clarify?.[currentQuestion.language]
          ? {
              isDefault: clarify[currentQuestion.language].isDefault ?? true,
              value: convertTexts(clarify[currentQuestion.language] ?? []),
            }
          : { isDefault: true, value: [] };

        state.byId[questionId] = {
          ...currentQuestion,
          clarify: { ...originalText },
        };
      }
    }
  },

  setText: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; language: Partial<TAvailableLanguageCodes> }>,
  ) => {
    const { questionId, language } = action.payload;

    const { intros, texts, outros, clarifies, questionConfig } = state.byId[questionId];

    if (state.byId[questionId]) {
      state.byId[questionId] = {
        ...state.byId[questionId],
        intro: intros?.[language] ?? {
          isDefault: questionConfig?.intro?.[language]?.isDefault ?? true,
          value: convertTexts(questionConfig?.intro?.[language] ?? []),
        },
        text: texts?.[language] ?? {
          isDefault: questionConfig?.text?.[language]?.isDefault ?? true,
          value: convertTexts(questionConfig?.text?.[language] ?? []),
        },
        outro: outros?.[language] ?? {
          isDefault: questionConfig?.outro?.[language]?.isDefault ?? true,
          value: convertTexts(questionConfig?.outro?.[language] ?? []),
        },
        clarify: clarifies?.[language] ?? {
          isDefault: questionConfig?.clarify?.[language]?.isDefault ?? true,
          value: convertTexts(questionConfig?.clarify?.[language] ?? []),
        },
      };
    }
  },

  addText: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      textType: TextType;
      value: any;
    }>,
  ) => {
    const { questionId, textType, value } = action.payload;

    if (!state.byId[questionId]) return;

    state.byId[questionId][textType].value = value;
    state.byId[questionId][textType].isDefault = true;
    state.byId[questionId].stateModified = true;
  },

  editText: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      textType: TextType;
      value: any;
    }>,
  ) => {
    const { questionId, textType, value } = action.payload;

    if (!state.byId[questionId]) return;

    state.byId[questionId][textType].value = value;
    state.byId[questionId][textType].isDefault = true;
    state.byId[questionId].stateModified = true;
  },

  removeText: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      textType: TextType;
      value: any;
    }>,
  ) => {
    const { questionId, textType, value } = action.payload;

    if (!state.byId[questionId]) return;

    state.byId[questionId][textType].value = value;
    state.byId[questionId][textType].isDefault = true;
    state.byId[questionId].stateModified = true;
  },

  moveText: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      from: TextType;
      to: TextType;
      fromIndex: number;
      toIndex: number;
    }>,
  ) => {
    const { questionId, from, to, fromIndex, toIndex } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];
    const fromArray = [...question[from].value];
    const toArray = [...question[to].value];

    if (from === to) {
      const text = fromArray.splice(fromIndex, 1)[0];
      fromArray.splice(toIndex, 0, text);
      question[from].value = fromArray;
    } else {
      const item = fromArray[fromIndex];
      fromArray.splice(fromIndex, 1);
      toArray.splice(toIndex, 0, item);
      question[from].value = fromArray;
      question[to].value = toArray;
    }
    question.stateModified = true;
  },

  dragAndDropText: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      items: TSortableInput;
      type: EQuestionType;
    }>,
  ) => {
    const { questionId, items, type } = action.payload;

    if (!state.byId[questionId]) return;

    const question = state.byId[questionId];

    const text = items['text'];
    const intro = items['intro'];
    const outro = items['outro'];
    const clarify = items['clarify'];

    if (type === EQuestionType.Info) {
      if (!text?.items) {
        return;
      }

      question.text = {
        isDefault: true,
        value: text.items,
      };
    } else if (type === EQuestionType.Open) {
      if (!intro?.items || !text?.items || !outro?.items || !clarify?.items) {
        return;
      }

      question.intro = {
        isDefault: true,
        value: intro.items,
      };
      question.text = {
        isDefault: true,
        value: text.items,
      };
      question.outro = {
        isDefault: true,
        value: outro.items,
      };
      question.clarify = {
        isDefault: true,
        value: clarify.items,
      };
    } else {
      if (!intro?.items || !text?.items || !outro?.items) {
        return;
      }

      question.intro = {
        isDefault: true,
        value: intro.items,
      };
      question.text = {
        isDefault: true,
        value: text.items,
      };
      question.outro = {
        isDefault: true,
        value: outro.items,
      };
    }
    question.stateModified = true;
  },
};
